import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';

const pricingModels = [
  { value: 'per_person', label: 'Per Person' },
  { value: 'flat_fee', label: 'Flat Fee' },
  { value: 'per_hour', label: 'Per Hour' },
  { value: 'custom', label: 'Custom' }
];

function Step6Pricing({ formData, businessId, onComplete, onError }) {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pricingData, setPricingData] = useState({
    startingPrice: formData.startingPrice || '',
    maxPrice: formData.maxPrice || '',
    pricingModel: formData.pricingModel || 'flat_fee',
    pricingNotes: formData.pricingNotes || ''
  });

  const handleChange = (field) => (event) => {
    setPricingData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!businessId) {
      setError('Business ID is required. Please complete previous steps first.');
      return;
    }

    // Validate starting price
    if (!pricingData.startingPrice || parseFloat(pricingData.startingPrice) < 0) {
      setError('Please enter a valid starting price');
      return;
    }

    // Validate maxPrice if provided
    if (pricingData.maxPrice) {
      const maxPriceValue = parseFloat(pricingData.maxPrice);
      const startingPriceValue = parseFloat(pricingData.startingPrice);
      if (maxPriceValue < 0) {
        setError('Max price must be a positive number');
        return;
      }
      if (maxPriceValue < startingPriceValue) {
        setError('Max price must be greater than or equal to starting price');
        return;
      }
    }

    try {
      setLoading(true);
      
      await api.put(`/api/supplier/business/${businessId}/pricing`, {
        startingPrice: parseFloat(pricingData.startingPrice),
        maxPrice: pricingData.maxPrice ? parseFloat(pricingData.maxPrice) : undefined,
        currency: 'GBP',
        pricingModel: pricingData.pricingModel,
        pricingNotes: pricingData.pricingNotes || undefined
      });
      
      onComplete({
        ...formData,
        startingPrice: parseFloat(pricingData.startingPrice),
        maxPrice: pricingData.maxPrice ? parseFloat(pricingData.maxPrice) : undefined,
        currency: 'GBP',
        pricingModel: pricingData.pricingModel,
        pricingNotes: pricingData.pricingNotes
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update pricing';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, ...styles.text.primary }}>
        Give couples a feel for your pricing
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, ...styles.text.secondary }}>
        Just a starting point – you can share full pricing later.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Starting Price"
              type="number"
              value={pricingData.startingPrice}
              onChange={handleChange('startingPrice')}
              required
              variant="outlined"
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Minimum price for your services"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Max Price (Optional)"
              type="number"
              value={pricingData.maxPrice}
              onChange={handleChange('maxPrice')}
              variant="outlined"
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Maximum price (must be ≥ starting price)"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Pricing Model</InputLabel>
              <Select
                value={pricingData.pricingModel}
                onChange={handleChange('pricingModel')}
                label="Pricing Model"
              >
                {pricingModels.map((model) => (
                  <MenuItem key={model.value} value={model.value}>
                    {model.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Pricing Notes (Optional)"
              value={pricingData.pricingNotes}
              onChange={handleChange('pricingNotes')}
              variant="outlined"
              multiline
              rows={2}
              placeholder="e.g., Starting from £5,000 for full day coverage"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Continue'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default Step6Pricing;

